import {
  IAIVideoModel,
  IAIVideoProject,
  IReferenceMediaImage,
  ReferenceMediaImageType
} from 'types/aiVideo'
import {
  mapSentPositionsToDisplayNumbers,
  restoreMentionsFromResolved
} from 'utils/promptMentions'
import { filterValidImages } from 'utils/aiImage'
import { getDefaultAIVideoModel } from '../utils'
import { DEFAULT_MEDIA_DURATION } from 'consts'

// Filters out reference images that may have been deleted from user's files
const filterExistingImages = async (
  images: IReferenceMediaImage[]
): Promise<IReferenceMediaImage[]> => {
  const results = await Promise.all(
    images.map(async image => {
      const validImages = await filterValidImages([image.url])
      return validImages.length > 0 ? image : null
    })
  )
  return results.filter(image => image !== null)
}

const mentionableUrls = (project?: IAIVideoProject): string[] =>
  (project?.referenceMedias?.images ?? [])
    .filter(image => image.type === ReferenceMediaImageType.REFERENCE_IMAGE)
    .map(image => image.url)

/**
 * Rebuild a stored prompt's `@Image N` chips when refilling the generate form.
 *
 * Only the RESOLVED wording (`reference image N`) is persisted, so a regeneration
 * form would otherwise show bare prose where the user had chips. The numbering
 * has to be remapped rather than reused: `filterDeletedReferenceImages` drops
 * reference images that no longer exist, which shifts the surviving ones' display
 * positions. A mention whose image is gone stays as literal words.
 *
 * `sentProject` is the UNFILTERED project (the cache keeps it — see
 * `fetchAIVideoProject`, which caches the raw data and only filters its return
 * value); without it the sent numbering is unknowable, so the prompt is left as
 * prose rather than risking chips that point at the wrong image.
 *
 * Frames are not mentionable, so a frames-only item yields no numbers and its
 * prompt is untouched.
 */
export const restoreFormPromptMentions = (params: {
  prompt: string
  sentProject: IAIVideoProject | undefined
  loadedProject: IAIVideoProject
}): string => {
  const { prompt, sentProject, loadedProject } = params

  if (!prompt || !sentProject) {
    return prompt
  }

  return restoreMentionsFromResolved({
    text: prompt,
    displayNumberBySentPosition: mapSentPositionsToDisplayNumbers({
      sentUrls: mentionableUrls(sentProject),
      loadedUrls: mentionableUrls(loadedProject)
    })
  })
}

export const filterDeletedReferenceImages = async (
  project: IAIVideoProject
): Promise<IAIVideoProject> => {
  if (!project?.referenceMedias?.images) {
    return project
  }

  return {
    ...project,
    referenceMedias: {
      ...project.referenceMedias,
      images: await filterExistingImages(project.referenceMedias.images)
    }
  }
}

export const getVerifiedModel = (
  modelName: string,
  aiVideoModels: IAIVideoModel[]
) => {
  const model = aiVideoModels.find(model => model.model === modelName)
  return model || getDefaultAIVideoModel(aiVideoModels)
}

export const getClosestPossibleVideoDuration = (
  duration?: number,
  selectedModel?: IAIVideoModel
) => {
  if (!duration || !selectedModel) {
    return DEFAULT_MEDIA_DURATION
  }
  const possibleDurations = selectedModel.options.map(option => option.duration)
  return possibleDurations.reduce((closest, current) => {
    return Math.abs(current - duration) < Math.abs(closest - duration)
      ? current
      : closest
  }, possibleDurations[0])
}
